/**
 * Inky turns on the server agent (supabase/functions/inky-agent). The browser sends one message
 * with the page context and the client tools it can apply, then folds the SSE stream into the
 * shared turn registry: text (coalesced per animation frame by inkyStream.js), tool steps, and
 * client actions it applies through the editor bridge (suggestions with review) or the router.
 *
 * One semantic on both sides (Flint's detach-not-abort):
 *  - A dropped stream never stops the server. After turn_start the turn is DETACHED: it keeps
 *    the composer guard and the thread is polled until the server's reply lands (the server
 *    sends its maximum turn length on turn_start, which bounds the wait).
 *  - Stop is explicit. Before turn_start nothing is confirmed, so the request is ended here and
 *    the server is told to leave no trace (`discard`). After turn_start Stop is sent to the
 *    server, the turn shows "Stopping" (the server still holds the conversation), the stream
 *    keeps being read, and the server's `done {stopped}` carries the saved partial reply. Retry
 *    then removes that reply and re-runs the same user message.
 *
 * Other outcomes:
 *  - 409: another turn holds this conversation; the message was never sent, so it goes back in
 *    the composer with the busy sentence.
 *  - 5xx / 404 / unreachable BEFORE the stream starts: the server agent is unavailable; the
 *    caller falls back to the browser loop (wave 1) for this turn and for a cool-down after.
 */

import edgeFunctionService from "../services/edgeFunctionService";
import chatAgentService from "../services/chatAgentService";
import { stableSeed } from "../constants/aiModels";
import { applyTurnEvent, detachTurn, failTurn, finishTurn, getTurn, isTurnActive, markTurnStopped, markTurnStopping } from "./assistantTurns";
import { readInkyStream, STREAM_DROPPED } from "./inkyStream";
import { applyClientAction, patchCardsWithOutcomes } from "./clientActions";
import { clientToolsFor } from "./surfaceBridge";

/** Poll cadence for a detached turn. */
export const DETACHED_POLL_MS = 3000;
/** The server's maximum turn length when it does not say (inky-agent INKY_HARD_LIMIT_MS default, plus setup). */
export const DEFAULT_MAX_TURN_MS = 150_000;
/** Extra patience past the server's maximum turn length (persisting, a slow network). */
export const DETACHED_MARGIN_MS = 30_000;
/** After Stop: send it once more (a Stop can lose a race with the lease heartbeat). */
const STOP_RESEND_MS = 6000;
/** After Stop: stop reading a stream that has not ended by then. */
const STOP_STREAM_GRACE_MS = 20_000;
/** After Stop, once the stream is gone: how long to look for the server's stopped reply. */
const STOPPED_POLL_WAIT_MS = 30_000;
/** Cool-down after the server agent was unreachable (then it is tried again). */
const UNAVAILABLE_COOLDOWN_MS = 3 * 60_000;

let unavailableUntil = 0;

/**
 * Whether turns should go to the server agent now.
 * @returns {boolean} True unless disabled by env or cooling down after a failure.
 */
export const serverAgentEnabled = () => process.env.REACT_APP_INKY_SERVER_AGENT !== "off" && Date.now() >= unavailableUntil;

/**
 * Stop sending turns to the server agent for a while.
 * @param {number} [ms] - Cool-down; Infinity for the rest of the session.
 * @param {string} [reason] - Logged reason.
 * @returns {void}
 */
export const markServerAgentUnavailable = (ms = UNAVAILABLE_COOLDOWN_MS, reason = "") => {
  unavailableUntil = ms === Infinity ? Number.MAX_SAFE_INTEGER : Date.now() + ms;
  console.warn(`[inky] server agent unavailable${reason ? ` (${reason})` : ""}; using the in-browser loop`);
};

/** Reset availability (tests, sign-in). */
export const resetServerAgentAvailability = () => {
  unavailableUntil = 0;
};

/**
 * Sleep that ends early on abort.
 * @param {number} ms - Milliseconds.
 * @param {AbortSignal} [signal] - Abort signal.
 * @returns {Promise<void>} Resolves after `ms` or on abort.
 */
const sleep = (ms, signal) =>
  new Promise((resolve) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener?.("abort", () => {
      clearTimeout(id);
      resolve();
    }, { once: true });
  });

/**
 * Read a JSON error body (best effort).
 * @param {Response} res - Response.
 * @returns {Promise<object>} Body or {}.
 */
const readJson = async (res) => {
  try {
    return (await res.json()) || {};
  } catch {
    return {};
  }
};

/**
 * Poll the thread for the assistant reply that follows a user message.
 * @param {object} params - Inputs.
 * @param {object} params.runtime - Turn runtime (fetchMessagesAfter, ingestMessage).
 * @param {string} params.conversationId - Conversation id.
 * @param {number} params.afterSequence - Sequence number of the user message.
 * @param {number} params.waitMs - Patience.
 * @param {AbortSignal} [params.signal] - Ends the wait.
 * @param {number} [params.intervalMs] - Poll cadence.
 * @param {() => boolean} [params.active] - The wait ends as soon as this returns false.
 * @returns {Promise<object|null>} The reply row, or null.
 */
export const pollForReply = async ({ runtime, conversationId, afterSequence, waitMs, signal, intervalMs = DETACHED_POLL_MS, active = () => true }) => {
  const deadline = Date.now() + waitMs;
  while (Date.now() < deadline) {
    await sleep(intervalMs, signal);
    if (signal?.aborted || !active()) return null;
    let rows = [];
    try {
      rows = (await runtime.fetchMessagesAfter(conversationId, afterSequence)) || [];
    } catch (error) {
      console.warn("[inky] could not read the thread", error);
      continue;
    }
    rows.forEach((row) => runtime.ingestMessage(row));
    const reply = rows.find((row) => row.role === "assistant");
    if (reply) return reply;
  }
  return null;
};

/**
 * Whether a saved reply is the server's stopped reply.
 * @param {object|null} row - Assistant row.
 * @returns {boolean} True when the turn was stopped.
 */
const isStoppedReply = (row) => row?.tool_call?.stopped === true;

/**
 * Run one turn on the server agent.
 * @param {object} params - Turn inputs.
 * @param {string} params.key - Turn key (already begun in the registry).
 * @param {string} params.text - User message.
 * @param {AbortSignal} params.signal - Turn signal (fires on Stop, or when the turn is dropped).
 * @param {object} params.runtime - Live accessors from the panel: resolveTarget, getPageContext,
 *   adoptConversation, ingestMessage, updateMessage, fetchMessagesAfter, getConversationTitle,
 *   updateConversationTitle, getEditorRef, navigate, openDocument, accountId, newConversationTitle,
 *   pollIntervalMs (optional, detached-turn polling cadence).
 * @param {object|null} [params.existingUserMessage] - Saved user message (retry/regenerate).
 * @param {number} [params.variant] - Regenerate counter.
 * @returns {Promise<'handled'|'fallback'>} 'fallback' when the server agent is unavailable and nothing was sent.
 */
/** Data kinds refreshed after the server saved something directly. */
const SAVED_CARD_KINDS = {
  "data.create_customer": "customer",
  "data.create_project": "project",
  "data.link_sessions": "session",
};

/**
 * Tell the app (lists, sidebar, Home) about customers, projects and meeting links the server
 * agent saved directly this turn, so links on the Done cards open real pages.
 * @param {object|null} toolCall - Saved reply's tool_call.
 * @returns {void}
 */
export const announceServerSaves = (toolCall) => {
  const kinds = new Set();
  for (const card of toolCall?.cards || []) {
    if (card?.status === "done" && SAVED_CARD_KINDS[card.tool]) kinds.add(SAVED_CARD_KINDS[card.tool]);
  }
  for (const kind of kinds) {
    try {
      window.dispatchEvent(new CustomEvent("quillai:data-changed", { detail: { kind, source: "inky" } }));
    } catch {
      // Non-browser environments: nothing listens.
    }
  }
};

export const runServerTurn = async ({ key, text, signal, runtime, existingUserMessage = null, variant = 0 }) => {
  const retryText = text || existingUserMessage?.content || "";
  if (signal.aborted) {
    markTurnStopped(key, { retry: { text: retryText, userMessage: existingUserMessage, variant } });
    return "handled";
  }
  const target = existingUserMessage
    ? { conversationId: existingUserMessage.conversation_id, contextKey: existingUserMessage.assistant_state?.contextKey || null }
    : await runtime.resolveTarget();
  if (signal.aborted) {
    markTurnStopped(key, { retry: { text: retryText, userMessage: existingUserMessage, variant } });
    return "handled";
  }
  const pageContext = runtime.getPageContext();
  const body = {
    conversationId: target.conversationId || undefined,
    ...(existingUserMessage ? { existingUserMessageId: existingUserMessage.id } : { message: text }),
    pageContext,
    clientTools: clientToolsFor(pageContext.surface),
    accountId: runtime.accountId || undefined,
    contextKey: target.contextKey || undefined,
    newConversationTitle: runtime.newConversationTitle || undefined,
    variant,
    turnKey: key,
  };

  let userRow = existingUserMessage;
  let started = false;
  let conversationId = target.conversationId || null;
  let maxTurnMs = DEFAULT_MAX_TURN_MS;
  let stopRequested = false;
  /** Retry re-runs the saved user message (the server sees it as the last row). */
  const retryInfo = () => ({ retry: { text: retryText, userMessage: userRow, variant } });
  /** Retry sends the text again as a new message (nothing of this turn is known to be saved). */
  const resendInfo = () => ({ retry: { text: retryText, userMessage: existingUserMessage, variant } });

  // The request is ended by this controller only: a dropped stream or Stop never aborts it
  // directly once the turn started (see the module comment).
  const fetchAbort = new AbortController();
  const timers = [];
  const sendStop = (discard) =>
    Promise.resolve()
      .then(() => edgeFunctionService.stopInkyAgent({ turnId: key, conversationId, discard }))
      .catch(() => ({ ok: false }));
  const onUserStop = () => {
    if (stopRequested) return;
    stopRequested = true;
    if (!started) {
      // Nothing is confirmed yet: end the request here, and have the server leave no trace.
      sendStop(true);
      fetchAbort.abort();
      return;
    }
    markTurnStopping(key);
    sendStop(false).then((outcome) => {
      // The server could not take the Stop: ending the request is all that is left.
      if (!outcome?.ok) fetchAbort.abort();
    });
    timers.push(setTimeout(() => sendStop(false), STOP_RESEND_MS));
    timers.push(setTimeout(() => fetchAbort.abort(), STOP_STREAM_GRACE_MS));
  };
  signal.addEventListener("abort", onUserStop, { once: true });

  /**
   * Settle the turn from a reply row found in the thread.
   * @param {object} reply - Assistant row.
   * @returns {void}
   */
  const settleFromReply = (reply) => {
    if (isStoppedReply(reply)) markTurnStopped(key, { messageId: reply.id, ...retryInfo() });
    else finishTurn(key, { messageId: reply.id });
  };

  try {
    let res;
    try {
      res = await edgeFunctionService.postInkyAgent(body, fetchAbort.signal);
    } catch (error) {
      if (stopRequested || error?.name === "AbortError") {
        markTurnStopped(key, resendInfo());
        return "handled";
      }
      if (error?.code === "HTTP_401") {
        failTurn(key, "Your session expired. Sign in again and retry.", resendInfo());
        return "handled";
      }
      markServerAgentUnavailable(UNAVAILABLE_COOLDOWN_MS, error?.message || "network");
      return "fallback";
    }

    const contentType = res.headers.get("content-type") || "";
    if (!res.ok || !contentType.includes("text/event-stream")) {
      const data = await readJson(res);
      if (res.status === 409) {
        failTurn(key, data.error || "Inky is still answering this conversation. Send that again when it finishes.", {
          retryable: false,
          busy: true,
          restoreText: existingUserMessage ? null : text,
        });
        return "handled";
      }
      if (res.status === 401) {
        failTurn(key, "Your session expired. Sign in again and retry.", resendInfo());
        return "handled";
      }
      if (res.status === 400 || (res.status === 404 && data.code === "CONVERSATION_NOT_FOUND")) {
        failTurn(key, data.error || "That message could not be sent.", { retryable: false, restoreText: existingUserMessage ? null : text });
        return "handled";
      }
      // Not deployed (gateway 404), restarting (5xx) or not our response: use the browser loop.
      markServerAgentUnavailable(res.status === 404 ? Infinity : UNAVAILABLE_COOLDOWN_MS, `HTTP ${res.status}`);
      return "fallback";
    }

    let createdConversation = false;
    const outcomes = new Map();
    const actionEnv = {
      getEditorRef: runtime.getEditorRef,
      navigate: runtime.navigate,
      openDocument: runtime.openDocument,
      markNavigated: () => applyTurnEvent(key, { type: "navigated" }),
    };

    const onEvent = (event) => {
      switch (event.type) {
        case "turn_start":
          started = true;
          conversationId = event.conversationId || conversationId;
          if (Number(event.maxTurnMs) > 0) maxTurnMs = Number(event.maxTurnMs);
          if (event.conversation) {
            createdConversation = true;
            runtime.adoptConversation(event.conversation, target.contextKey);
          }
          if (event.userMessage) {
            userRow = event.userMessage;
            runtime.ingestMessage(event.userMessage);
          }
          applyTurnEvent(key, { type: "conversation", conversationId, userMessageId: event.userMessage?.id || null });
          applyTurnEvent(key, { type: "phase", phase: "thinking" });
          break;
        case "text_delta":
          applyTurnEvent(key, { type: "text_delta", delta: event.text });
          break;
        case "text_reset":
          applyTurnEvent(key, { type: "text_reset" });
          break;
        case "tool_start":
          applyTurnEvent(key, { type: "step_start", step: { id: event.id, tool: event.name, label: event.label || event.name } });
          break;
        case "tool_end":
          applyTurnEvent(key, { type: "step_end", id: event.id, status: event.ok ? "done" : "error", summary: event.summary });
          applyTurnEvent(key, { type: "phase", phase: "thinking" });
          break;
        case "client_action": {
          const outcome = applyClientAction(event, actionEnv);
          if (event.kind === "doc_edit_plan" && event.payload?.cardId) outcomes.set(event.payload.cardId, outcome);
          if (!outcome.ok) console.warn("[inky] client action not applied", event.kind, outcome.error);
          break;
        }
        default:
          break;
      }
    };

    let terminal;
    try {
      terminal = await readInkyStream(res.body, onEvent);
    } catch (error) {
      terminal = { type: "error", code: STREAM_DROPPED, message: String(error?.message || "") };
    }

    if (terminal.type === "done") {
      if (terminal.discarded) {
        markTurnStopped(key, resendInfo());
        return "handled";
      }
      let saved = terminal.message || null;
      if (saved && !saved.id && conversationId) {
        // The server could not persist the reply: still show it (it will not survive a reload).
        runtime.ingestMessage({
          ...saved,
          id: `local-${key}`,
          conversation_id: conversationId,
          sequence_number: (Number(userRow?.sequence_number) || 0) + 1,
        });
      }
      announceServerSaves(saved?.tool_call);
      if (saved?.id) {
        runtime.ingestMessage(saved);
        const patched = patchCardsWithOutcomes(saved.tool_call, outcomes);
        if (patched) {
          saved = { ...saved, tool_call: patched };
          runtime.updateMessage(saved.id, { tool_call: patched }).catch((error) => console.warn("[inky] could not save edit outcomes", error));
        }
      }
      if (terminal.stopped) {
        // Retry removes the saved stopped reply (messageId) and re-runs the same user message.
        markTurnStopped(key, { messageId: saved?.id || null, ...retryInfo() });
        return "handled";
      }
      finishTurn(key, { messageId: saved?.id || null });
      if (createdConversation && conversationId && !existingUserMessage) {
        chatAgentService
          .suggestTitle({ userText: text, replyText: saved?.content || "", seed: stableSeed("chat.title", conversationId) })
          .then((title) => title && runtime.updateConversationTitle(conversationId, title))
          .catch(() => {});
      }
      return "handled";
    }

    if (terminal.code === STREAM_DROPPED && !started) {
      if (stopRequested) markTurnStopped(key, resendInfo());
      else failTurn(key, "The connection dropped before Inky started. Try again.", resendInfo());
      return "handled";
    }

    if (terminal.code === STREAM_DROPPED && conversationId && userRow?.sequence_number != null) {
      const stillOurs = () => isTurnActive(getTurn(key));
      if (stopRequested) {
        // We stopped reading after asking the server to stop: its stopped reply lands shortly.
        const reply = await pollForReply({
          runtime,
          conversationId,
          afterSequence: userRow.sequence_number,
          waitMs: STOPPED_POLL_WAIT_MS,
          intervalMs: Math.min(runtime.pollIntervalMs || 1500, 1500),
          active: stillOurs,
        });
        if (!stillOurs()) return "handled";
        // The server may have finished before the Stop reached it: a full answer is a done turn.
        if (reply) settleFromReply(reply);
        else markTurnStopped(key, resendInfo());
        return "handled";
      }
      // The server keeps working without us: wait for its reply in the thread.
      detachTurn(key);
      const reply = await pollForReply({
        runtime,
        conversationId,
        afterSequence: userRow.sequence_number,
        waitMs: maxTurnMs + DETACHED_MARGIN_MS,
        intervalMs: runtime.pollIntervalMs || DETACHED_POLL_MS,
        active: stillOurs,
      });
      if (!stillOurs()) return "handled";
      if (reply) settleFromReply(reply);
      else if (stopRequested) markTurnStopped(key, resendInfo());
      else failTurn(key, "The connection dropped and the reply did not arrive. Reload the conversation, or ask again.", retryInfo());
      return "handled";
    }

    if (stopRequested && terminal.code === STREAM_DROPPED) {
      markTurnStopped(key, resendInfo());
      return "handled";
    }
    failTurn(key, terminal.message || "Something went wrong while answering. Try again.", { retryable: terminal.code !== "STALE_RETRY", ...retryInfo() });
    return "handled";
  } finally {
    signal.removeEventListener("abort", onUserStop);
    timers.forEach((id) => clearTimeout(id));
  }
};
